import React, { useEffect, useRef } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

// Global registry to track all active players
const activePlayersRegistry = new Set();

/**
 * VideoJS Player Component
 * Handles video playback with auto-stop functionality for other videos
 */
const VideoPlayer = ({ src, onReady, ...otherProps }) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    // Make sure Video.js player is only initialized once
    if (!playerRef.current && videoRef.current) {
      const videoElement = videoRef.current;

      const player = playerRef.current = videojs(videoElement, {
        controls: true,
        responsive: true,
        fluid: false,
        preload: 'metadata',
        playbackRates: [0.5, 1, 1.5, 2],
        controlBar: {
          volumePanel: {
            inline: false
          }
        }
      }, () => {
        if (onReady) {
          onReady(player);
        }
      });

      // Register this player
      activePlayersRegistry.add(player);

      // When this player starts playing, pause all other players
      player.on('play', () => {
        activePlayersRegistry.forEach((otherPlayer) => {
          if (otherPlayer !== player && !otherPlayer.paused()) {
            otherPlayer.pause();
          }
        });
      });

      // Handle buffering/loading state
      player.on('waiting', () => {
        console.log('Video is buffering...');
      });

      // player.on('canplay', () => {
      // });

    } else if (playerRef.current && src) {
      // Update source if it changes
      const player = playerRef.current;
      player.src({ src, type: 'video/mp4' });
    }
  }, [src, onReady]);

  // Dispose the Video.js player when the component unmounts
  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        // Remove from registry
        activePlayersRegistry.delete(player);
        // Pause before disposing
        player.pause();
        player.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  return (
    <div data-vjs-player>
      <video
        ref={videoRef}
        className="video-js vjs-big-play-centered"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain'
        }}
        {...otherProps}
      >
        <source src={src} type="video/mp4" />
      </video>
    </div>
  );
};

export default VideoPlayer;
